import React, { useEffect } from 'react';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import BasicModal from '../../../components/BasicModal';
import MyDatePicker from '../../../components/MyDatePicker';
import { accessService, InvalidIpAddress, IpAddressAlreadyExists } from '../../../services/central-api/access';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import { getFormattedAvailableIPCount } from '../../../utils/getAvailableIpsCount';
import { useForm } from 'react-hook-form';
import { View } from 'react-native';
import moment from 'moment';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    allowedIpId?: number;
}

interface AllowedIpForm {
    ipAddress: string;
    description: string;
    applicant: string;
    accessGrantedIps: string;
    expiresAt: Date | null;
}

const defaultEmptyValues: AllowedIpForm = {
    ipAddress: '',
    description: '',
    applicant: '',
    accessGrantedIps: '',
    expiresAt: null,
};

function fromDateOnlyString(value?: string | null): Date | null {
    if (!value) {
        return null;
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.toDate() : null;
}

function toDateOnlyString(date: Date | null): string | null {
    if (!date) {
        return null;
    }
    return moment(date).endOf('day').toISOString();
}

function isIpAddressInvalid(value: string) {
    return !value || getFormattedAvailableIPCount(value) === 'Invalid';
}

export default function AllowedIpModal({ onClose, allowedIpId, isModalVisible }: ModalProps) {
    const form = useForm<AllowedIpForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    async function getAllowedIp(id: number) {
        const allowedIp = await accessService.getAllowedIp(id);
        form.reset({
            ...defaultEmptyValues,
            ipAddress: allowedIp.ipAddress,
            description: allowedIp.description,
            applicant: allowedIp.applicant,
            expiresAt: fromDateOnlyString(allowedIp.expiresAt),
        });
    }

    async function loadPageInfo() {
        try {
            if (allowedIpId) {
                await getAllowedIp(allowedIpId);
            }
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    const ipAddress = form.watch('ipAddress');
    useEffect(() => {
        if (!ipAddress) {
            form.setValue('accessGrantedIps', '');
            return;
        }
        const availableIps = getFormattedAvailableIPCount(ipAddress);
        form.setValue(
            'accessGrantedIps',
            availableIps === 'Invalid' ? translate(availableIps) : availableIps
        );
    }, [ipAddress]);

    function clearValues() {
        form.reset(defaultEmptyValues);
    }

    async function formSubmit() {
        try {
            const values = form.getValues();
            const expiresAt = toDateOnlyString(values.expiresAt);

            if (allowedIpId) {
                await accessService.updateAllowedIp({
                    id: allowedIpId,
                    ipAddress: values.ipAddress,
                    description: values.description,
                    expiresAt,
                });
            } else {
                await accessService.createAllowedIp({
                    ipAddress: values.ipAddress,
                    description: values.description,
                    applicant: values.applicant,
                    expiresAt,
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: `${translate('ActionSuccessfully')}\n\n${translate('actionWillTakeFewMinutes')}`,
                visibilityTime: 10000,
            });
        } catch (err) {
            if (err instanceof InvalidIpAddress || err instanceof IpAddressAlreadyExists) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message, { ipAddress: err.ipAddress }),
                    visibilityTime: 10000,
                });
            } else if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            clearValues();
        }
    }

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={(visible) => {
                if (!visible) {
                    onClose();
                }
            }}
            width='small'
            title={translate(allowedIpId ? 'editAllowedIp' : 'addAllowedIp')}
            primaryButton={{
                text: translate('save'),
                onClick: formSubmit,
                disabled: !form.formState.isValid,
            }}
            cancelButton={{ onClick: clearValues }}
        >
            <View style={{ rowGap: 10 }}>
                <FormInput
                    form={form}
                    fieldName='ipAddress'
                    customValidations={{
                        isValid: (val) => !isIpAddressInvalid(val as string) || translate('invalid_field')
                    }}
                    label={translate('ipAddress')}
                    placeholder={translate('ipAddress')}
                    required
                />
                <FormInput
                    form={form}
                    fieldName='description'
                    label={translate('description')}
                    placeholder={translate('description')}
                    multiline={true}
                    numberOfLines={3}
                    required
                />
                <FormInput
                    form={form}
                    fieldName='applicant'
                    label={translate('applicant')}
                    placeholder={translate('applicant')}
                    required
                    disabled={!!allowedIpId}
                />
                <FormInput
                    form={form}
                    fieldName='accessGrantedIps'
                    label={translate('accessGrantedIps')}
                    placeholder={translate('accessGrantedIps')}
                    disabled={true}
                />
                <MyDatePicker
                    form={form}
                    dateFieldName='expiresAt'
                    label={translate('expirationDate')}
                    minDate={moment().startOf('day').toDate()}
                />
            </View>
        </BasicModal>
    );
}
